import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioTravelObject from '@/entities/ratioTravel/interface'

import {
  ALL_FIELD_VISIBILITY_CONFIGS_QUERY,
  SINGLE_FIELD_VISIBILITY_CONFIG_QUERY,
  FIELD_VISIBILITY_CONFIGS_DEPENDENTS_QUERY,
  CREATE_FIELD_VISIBILITY_CONFIG_MUTATION,
  UPDATE_FIELD_VISIBILITY_CONFIG_MUTATION,
  DELETE_FIELD_VISIBILITY_CONFIG_MUTATION,
  FORCE_DELETE_FIELD_VISIBILITY_CONFIG_MUTATION,
  ADD_FIELD_VISIBILITY_CONFIG_PRICING_TRAVELS_MUTATION,
  REMOVE_FIELD_VISIBILITY_CONFIG_PRICING_TRAVELS_MUTATION,
  CREATE_FIELD_VISIBILITY_CONFIG_PRICING_TRAVELS_MUTATION,
  DELETE_FIELD_VISIBILITY_CONFIG_PRICING_TRAVELS_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const customBookingFormFieldVisibilityConfigRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_FIELD_VISIBILITY_CONFIG_QUERY, variables).then((data: any) => {
      return hydrateObject(data.fieldVisibilityConfig)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_FIELD_VISIBILITY_CONFIGS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.fieldVisibilityConfigs)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_FIELD_VISIBILITY_CONFIG_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.fieldVisibilityConfig),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_FIELD_VISIBILITY_CONFIGS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.fieldVisibilityConfigs),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(FIELD_VISIBILITY_CONFIGS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([FIELD_VISIBILITY_CONFIGS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_FIELD_VISIBILITY_CONFIG_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.fieldVisibilityConfig)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_FIELD_VISIBILITY_CONFIG_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.fieldVisibilityConfig)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_FIELD_VISIBILITY_CONFIG_MUTATION, variables).then((data: any) => {
      return data.deleteCustomBookingFormFieldVisibilityConfig
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_FIELD_VISIBILITY_CONFIG_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCustomBookingFormFieldVisibilityConfig
    })
  },

  assignPricingTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pricingTravelIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_FIELD_VISIBILITY_CONFIG_PRICING_TRAVELS_MUTATION, variables)
  },

  unassignPricingTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pricingTravelIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_FIELD_VISIBILITY_CONFIG_PRICING_TRAVELS_MUTATION, variables)
  },

  createPricingTravels: async (id: string, items: Array<ratioTravelObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pricingTravels: items,
      },
    }
    return graphQLClient.request(CREATE_FIELD_VISIBILITY_CONFIG_PRICING_TRAVELS_MUTATION, variables)
  },

  deletePricingTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pricingTravelIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_FIELD_VISIBILITY_CONFIG_PRICING_TRAVELS_MUTATION, variables)
  },
}

export default customBookingFormFieldVisibilityConfigRepository
